import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SponsorHeroBanner = () => {
  const [sponsors, setSponsors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/sponsor-heroes`);
        if (response.ok) {
          const data = await response.json();
          setSponsors(data);
        }
      } catch (err) {
        console.error('Failed to fetch sponsor heroes:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  // Detect theme mode
  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setIsDarkMode(theme === 'dark' || theme === 'dim');
    };
    
    checkTheme();
    window.addEventListener('storage', checkTheme);
    
    // Also watch for class changes on html element
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => {
      window.removeEventListener('storage', checkTheme);
      observer.disconnect();
    };
  }, []);

  // Get the appropriate logo URL based on theme
  const getLogoUrl = (sponsor) => {
    if (isDarkMode) {
      return sponsor.logo_url_dark || sponsor.logo_url;
    }
    return sponsor.logo_url_light || sponsor.logo_url;
  };

  // Don't render if no sponsors or still loading
  if (isLoading || sponsors.length === 0) return null;

  return (
    <div 
      className="w-full bg-gradient-to-r from-primary/10 via-secondary/5 to-primary/10 border-b border-border/50 py-3"
      data-testid="sponsor-hero-banner"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-center gap-8 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium whitespace-nowrap">
            Proudly Supported By
          </span>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            {sponsors.map((sponsor) => (
              <a
                key={sponsor.id}
                href={sponsor.website || '#'}
                target={sponsor.website ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="group flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity"
                title={sponsor.name}
              >
                {getLogoUrl(sponsor) ? (
                  <img 
                    src={getLogoUrl(sponsor)} 
                    alt={sponsor.name} 
                    className="h-8 w-auto object-contain max-w-[120px]"
                  />
                ) : (
                  <span className="text-sm font-medium text-foreground/80 group-hover:text-foreground">
                    {sponsor.name}
                  </span>
                )}
                {sponsor.website && (
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                )}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorHeroBanner;
